import type { AuditLogEntry } from "@crewledger/shared/schemas";
import { cn } from "@/lib/utils";

function formatValue(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function collectKeys(
  before: Record<string, unknown> | null,
  after: Record<string, unknown> | null,
): string[] {
  const keys = new Set<string>();
  if (before) Object.keys(before).forEach((k) => keys.add(k));
  if (after) Object.keys(after).forEach((k) => keys.add(k));
  return Array.from(keys).sort();
}

export function AuditDiffViewer({ entry }: { entry: AuditLogEntry }) {
  if (!entry.hasDiff && !entry.before && !entry.after) {
    return (
      <p className="text-sm text-muted-foreground">
        {entry.reason ?? "No before/after snapshot recorded for this event."}
      </p>
    );
  }

  const keys = collectKeys(entry.before, entry.after);

  if (keys.length === 0) {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        {entry.before ? (
          <DiffPanel title="Before" content={formatValue(entry.before)} />
        ) : null}
        {entry.after ? (
          <DiffPanel title="After" content={formatValue(entry.after)} />
        ) : null}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full text-xs">
        <thead className="bg-bg-light text-left uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-semibold">Field</th>
            <th className="px-3 py-2 font-semibold">Before</th>
            <th className="px-3 py-2 font-semibold">After</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((key) => {
            const beforeVal = entry.before?.[key];
            const afterVal = entry.after?.[key];
            const changed = JSON.stringify(beforeVal) !== JSON.stringify(afterVal);

            return (
              <tr key={key} className={cn("border-t", changed && "bg-amber-50/50")}>
                <td className="px-3 py-2 font-mono font-medium text-navy-deep">{key}</td>
                <td className="px-3 py-2 font-mono text-muted-foreground">
                  {formatValue(beforeVal)}
                </td>
                <td
                  className={cn(
                    "px-3 py-2 font-mono",
                    changed ? "font-semibold text-navy-deep" : "text-muted-foreground",
                  )}
                >
                  {formatValue(afterVal)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DiffPanel({ title, content }: { title: string; content: string }) {
  return (
    <div className="rounded-lg border bg-bg-light">
      <p className="border-b px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </p>
      <pre className="max-h-64 overflow-auto p-3 font-mono text-xs text-navy-deep">{content}</pre>
    </div>
  );
}
